import { useAppDispatch } from '@/app/redux/hooks';
import { useAssessments } from '@/app/redux/hooks/assessments';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import { setAttemptedProFeature } from '@/app/redux/slices/modalsSlice';
import { Activity } from '@/common/types/session';
import {
  ContentPanelBody,
  ContentPanelCloseButton,
  ContentPanelCollapseButton,
  ContentPanelContainer,
  ContentPanelControls,
  ContentPanelHeader,
  ContentPanelLeft,
  ContentPanelRight,
  ContentPanelTabsTrigger,
  ContentPanelTitle,
} from '@/common/ui/ContentPanel';
import { useIsFeatureEnabled } from '@/common/ui/FeatureGate';
import ShareLinkDialogue from '@/common/ui/modals/ShareLinkDialogue';
import AssessmentDetails from '@/dashboard/Assessment/AssessmentDetails';
import AssessmentStudentRow from '@/dashboard/Assessment/AssessmentStudentRow';
import ActivityDialog from '@/dashboard/LearnerView/ActivityFeed/ActivityDialog';
import api from '@/db/apiClient';
import {
  CaretDoubleLeftIcon,
  CaretDoubleRightIcon,
  CaretDownIcon,
  CaretUpIcon,
  GearIcon,
  StudentIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@shadcn/components/table';
import { Tabs, TabsContent, TabsList } from '@shadcn/components/tabs';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

export default function AssessmentView() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isFeatureEnabled = useIsFeatureEnabled();
  const { assessmentId } = useParams();

  const { currentOrganisationId } = useCurrentOrganisationId();
  const { studentsById } = useStudents();

  const { assessments } = useAssessments();

  const [isLoading, setLoading] = useState(false);
  const [isRightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [activitiesByStudentId, setActivitiesByStudentId] = useState<
    Record<string, Activity | undefined>
  >({});
  const [selectedResult, setSelectedResult] = useState<{
    studentId: string;
    activity: Activity;
  } | null>(null);
  const [prevResult, setPrevResult] = useState<{
    studentId: string;
    activity: Activity;
  } | null>(null);
  const [shareLinkDialogueOpen, setShareLinkDialogueOpen] = useState(false);

  const assessmentIds = useMemo(
    () => assessments.map((a) => a.id),
    [assessments],
  );
  const selectedAssessment = useMemo(
    () => assessments.find((a) => a.id === assessmentId),
    [assessmentId, assessments],
  );
  const selectedAssessmentIdx = useMemo(
    () => (assessmentId ? assessmentIds.indexOf(assessmentId) : 0),
    [assessmentId, assessmentIds],
  );

  useEffect(() => {
    (async () => {
      if (!selectedAssessment || !currentOrganisationId) {
        setActivitiesByStudentId({});
        return;
      }
      setLoading(true);
      const activities = await api.asEducator.getActivitiesForAssessment(
        currentOrganisationId,
        selectedAssessment.id,
      );
      const activitiesByStudentId = activities.reduce<
        Record<string, Activity | undefined>
      >((acc, activity) => {
        // don't include partial completions
        if (!activity.endedAt) return acc;

        // only v3+ activities will be attached to assessments
        if (activity.version === 1 || activity.version === 2) return acc;

        return {
          ...acc,
          [activity.studentId]: activity,
        };
      }, {});
      setActivitiesByStudentId(activitiesByStudentId);
      setLoading(false);
    })();
  }, [currentOrganisationId, selectedAssessment]);

  function handleNavigateBack() {
    navigate('/dashboard/assessments');
  }

  function handleNavigatePrevious() {
    if (selectedAssessmentIdx === 0) {
      return;
    }
    navigate(
      `/dashboard/assessments/${assessmentIds[selectedAssessmentIdx - 1]}`,
    );
  }

  function handleNavigateNext() {
    if (selectedAssessmentIdx === assessmentIds.length - 1) {
      return;
    }
    navigate(
      `/dashboard/assessments/${assessmentIds[selectedAssessmentIdx + 1]}`,
    );
  }

  if (!selectedAssessment) return null;
  return (
    <ContentPanelContainer>
      <ContentPanelHeader>
        <ContentPanelTitle>
          <div className="flex items-baseline gap-2">
            Assessment
            <span className="text-sm text-muted-foreground">
              {dayjs(selectedAssessment.createdAt).format('DD/MM/YY h:mm a')}
            </span>
          </div>
        </ContentPanelTitle>

        <ContentPanelControls>
          <div className="flex gap-1">
            <Button
              className="px-1 py-1 h-fit"
              variant="outline"
              onClick={handleNavigatePrevious}
              disabled={selectedAssessmentIdx === 0}
            >
              <CaretUpIcon />
            </Button>
            <Button
              className="px-1 py-1 h-fit"
              variant="outline"
              onClick={handleNavigateNext}
              disabled={selectedAssessmentIdx === assessmentIds.length - 1}
            >
              <CaretDownIcon />
            </Button>
          </div>

          <span className="text-muted-foreground pointer-events-none">
            Assessment {selectedAssessmentIdx + 1} of {assessmentIds.length}
          </span>

          <ContentPanelCloseButton onClick={handleNavigateBack} />
        </ContentPanelControls>
      </ContentPanelHeader>

      <ContentPanelBody>
        <ContentPanelLeft className="flex-1">
          <Tabs defaultValue="learners" className="h-full w-full">
            <AssessmentContentHeader>
              <TabsList className="bg-white gap-1">
                <ContentPanelTabsTrigger value="learners">
                  <StudentIcon size={16} /> Learners
                </ContentPanelTabsTrigger>
              </TabsList>
            </AssessmentContentHeader>

            <AssessmentContentBody>
              <TabsContent value="learners" className="h-full">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="px-6">Learner</TableHead>
                      <TableHead className="pl-4">Status</TableHead>
                      <TableHead className="pl-4">Content</TableHead>
                      <TableHead className="pl-4">Control Type</TableHead>
                      <TableHead className="pl-4">Placement</TableHead>
                      <TableHead className="px-6">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(selectedAssessment.students)
                      .filter(([id]) => !!studentsById.get(id))
                      .map(([studentId, student]) => (
                        <AssessmentStudentRow
                          key={studentId}
                          studentId={studentId}
                          assessment={selectedAssessment}
                          assessmentStudent={student}
                          isLoading={isLoading}
                          hasResult={!!activitiesByStudentId[studentId]}
                          onViewResult={() => {
                            if (isFeatureEnabled('assessments')) {
                              const activity = activitiesByStudentId[studentId];
                              if (!activity) return;
                              setSelectedResult({
                                studentId: studentId,
                                activity,
                              });
                            } else {
                              dispatch(setAttemptedProFeature('Assessments'));
                            }
                          }}
                        />
                      ))}
                  </TableBody>
                </Table>
              </TabsContent>
            </AssessmentContentBody>
          </Tabs>
        </ContentPanelLeft>

        <ContentPanelRight
          className={isRightPanelCollapsed ? 'collapsed' : undefined}
        >
          <Tabs
            defaultValue="details"
            value={isRightPanelCollapsed ? '' : 'details'}
            className="content-panel-child"
          >
            <AssessmentContentHeader>
              <TabsList className="bg-white gap-1">
                <ContentPanelCollapseButton
                  onClick={() => setRightPanelCollapsed(!isRightPanelCollapsed)}
                  $isCollapsed={isRightPanelCollapsed}
                  $withButtons
                >
                  <ContentPanelTabsTrigger value="details" className="gap-2">
                    {!isRightPanelCollapsed && <CaretDoubleLeftIcon />}
                    <GearIcon size={18} /> Settings
                    {isRightPanelCollapsed && <CaretDoubleRightIcon />}
                  </ContentPanelTabsTrigger>
                </ContentPanelCollapseButton>
              </TabsList>
            </AssessmentContentHeader>

            <AssessmentDetailsBody>
              <TabsContent value="details">
                <AssessmentDetails assessment={selectedAssessment} />
              </TabsContent>
            </AssessmentDetailsBody>
          </Tabs>
        </ContentPanelRight>
      </ContentPanelBody>

      <ActivityDialog
        activity={selectedResult?.activity ?? null}
        studentId={selectedResult?.studentId ?? ''}
        onClose={() => setSelectedResult(null)}
        onShare={() => {
          setPrevResult(selectedResult);
          setSelectedResult(null);
          setShareLinkDialogueOpen(true);
        }}
      />

      {prevResult && (
        <ShareLinkDialogue
          open={shareLinkDialogueOpen}
          onClose={() => {
            setShareLinkDialogueOpen(false);
            setSelectedResult(prevResult);
            setPrevResult(null);
          }}
          studentId={prevResult.studentId}
          type="activity"
          resourceId={prevResult.activity.id}
        />
      )}
    </ContentPanelContainer>
  );
}

export function AssessmentsDisabledRedirect() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(setAttemptedProFeature('Assessments'));
    navigate('/dashboard/assessments');
  }, [dispatch, navigate]);

  return null;
}

const AssessmentContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid hsl(var(--border));
  padding: 4px 8px;
`;

const AssessmentContentBody = styled.div`
  overflow-y: auto;
  height: 100%;
`;

const AssessmentDetailsBody = styled.div`
  height: 100%;
`;
